import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_HISTORY_PATH_KEY } from '@/swagger/constants/credits-keys';
import { API_GET_EMPLOYER_CREDITS_HISTORY, KEY_GET_EMPLOYER_CREDITS_HISTORY } from '../constants';
import { MOCK_RESPONSE_GET_EMPLOYER_CREDITS_HISTORY } from '../mocks';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetEmployerCreditsHistory, 'successSaga' | 'errorSaga'>;
};

type RequestGetEmployerCreditsHistory = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_HISTORY_PATH_KEY
>['request']['query'];

type ResponseGetEmployerCreditsHistory = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_HISTORY_PATH_KEY
>['response']['body'];

export type CallParamsGetEmployerCreditsHistory = RequestGetEmployerCreditsHistory & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetEmployerCreditsHistory = ResponseGetEmployerCreditsHistory;

type CallErrorGetEmployerCreditsHistory = {
  message: string;
  code: number;
};

type CallSelectorsGetEmployerCreditsHistory = {
  data: ResponseGetEmployerCreditsHistory['data'];
  totalItems: ResponseGetEmployerCreditsHistory['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getEmployerCreditsHistoryCall = createGeneralCall<
  CallParamsGetEmployerCreditsHistory,
  CallResponseGetEmployerCreditsHistory,
  CallErrorGetEmployerCreditsHistory,
  CallSelectorsGetEmployerCreditsHistory
>({
  id: KEY_GET_EMPLOYER_CREDITS_HISTORY,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_EMPLOYER_CREDITS_HISTORY(org as unknown as string, payload);
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_EMPLOYER_CREDITS_HISTORY : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
});
